import { Box, Button, Flex, Text, Link as ExternalLink } from '@chakra-ui/react';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { ModalSubscribeVariantProps, SharedModalProps } from 'types/Modal';
import { ExternalRoutes } from 'types/Routes';
import { PaymentInterval } from '@novaheal/types';

import { useBreakpoint } from 'hooks/utils/layout/useBreakpoint';
import { Breakpoints } from 'types/breakpoints';
import { useCreateSubscription } from 'hooks/api/backend/subscription/useCreateSubscription';

import { SessionStorageKey, useSessionStorage } from 'hooks/useSessionStorage';
import { PromoCode } from 'types/PromoCode';
import { PresentIcon, SuccessIcon } from 'assets/icons/v2';
import { useUser } from 'utils/auth';
import { useReferralDetails } from 'hooks/api/backend/user/useReferral';
import { NovaIcon } from 'components/ui/novaIcon';
import { CustomModal } from '../../CustomModal';
import { SubscriptionBenefits } from './SubscriptionBenefits';
import { SubscriptionButtons } from './SubscriptionButtons';
import { SubscriptionSlider } from './SubscriptionSlider';
import { useSubscriptionPrices } from '../../../../../hooks/api/backend/subscription/useSubscriptionPrices';
import { useNovaToast } from '../../../../../hooks/utils/useNovaToast';
import { ToastVariant } from '../../../../../types/Toast';
import { logBrazeEvent } from '../../../../../utils/brazeEvents';

type Props = SharedModalProps & ModalSubscribeVariantProps;

export const SubscribeModal = ({ handleClose, isShown }: Props): ReactElement | null => {
  const [activeButton, setActiveButton] = useState<PaymentInterval>(PaymentInterval.YEARLY);
  const { isPending: isLoading, mutateAsync: mutateAsyncSubscription } =
    useCreateSubscription(activeButton);
  const toast = useNovaToast();
  const promoCode = useSessionStorage<PromoCode>(SessionStorageKey.PROMO_CODE).getValue();
  const isCouponExpired = promoCode?.expirationDate
    ? new Date(promoCode.expirationDate) < new Date()
    : false;
  const [shouldShowCouponBanner, setShouldShowCouponBanner] = useState(
    !!promoCode?.id && !isCouponExpired && !promoCode?.package
  );
  const { data: user } = useUser();

  const { data: referralInfos } = useReferralDetails(!!user?.referralCode);

  const { data: subscriptionPrices, isError: subscriptionPricesError } = useSubscriptionPrices();

  const isDesktop = useBreakpoint(Breakpoints.DESKTOP);
  const subscribeWidth = isDesktop ? '60%' : '100%';

  // This modal instance can be toggled via `isShown` without unmounting, so
  // track transitions into the shown state rather than relying on mount order.
  const wasShownRef = useRef(false);
  useEffect(() => {
    if (isShown && !wasShownRef.current) {
      logBrazeEvent('paywall_hit', { paywallLastViewedAt: new Date().toISOString() });
    }
    wasShownRef.current = isShown;
  }, [isShown]);

  useEffect(() => {
    if (subscriptionPricesError) {
      toast.show({
        variant: ToastVariant.ERROR,
      });
    }
  }, [subscriptionPricesError, toast]);

  useEffect(() => {
    if (promoCode) {
      // TODO: think how to implement a redemption limit without asking stripe for it (without info about redemption count)
      // const redemptionLimitReached = promoCode.maxRedemptions;
      // if (redemptionLimitReached) {
      //   toast.show({
      //     ...toastContent.subscription.couponValidations.couponRedemptionLimitReached,
      //   });
      // }
    }
  }, [promoCode, toast]);

  const handleSubscribe = async (): Promise<void> => {
    const activeButtonPackage = activeButton === PaymentInterval.YEARLY ? 'yearly' : 'monthly';
    const shouldAddPromoCode =
      (!!promoCode?.id && !promoCode?.package) ||
      (!!promoCode?.package && activeButtonPackage === promoCode?.package);
    const checkoutLink = await mutateAsyncSubscription(
      shouldAddPromoCode ? promoCode?.id : undefined
    );

    if (checkoutLink) window.location.replace(checkoutLink);
  };

  return (
    <CustomModal
      isOpen={isShown}
      onClose={handleClose}
      placement="center"
      hideHeader={isDesktop}
      isFullscreen={!isDesktop}
      size="xl"
      floatingCross={isDesktop}
    >
      {/*
        Dialog `scrollBehavior="inside"` only auto-scrolls the `Dialog.Body` slot; CustomModal
        renders children directly into `Dialog.Content`, so we need a flex child with minH={0}
        and overflowY="auto" to scroll within the content max-height.
      */}
      <Box flex="1" minH="0" minW="0" overflowY="auto" overscrollBehavior="contain" w="100%">
        <Flex flexDirection={{ base: 'column-reverse', md: 'row' }} width={subscribeWidth}>
          {!isDesktop && (
            <Box
              position="sticky"
              bottom="0"
              display="grid"
              placeItems="center"
              width="100%"
              background="linear-gradient(180deg, rgba(253, 252, 254, 0) 0%, #FDFCFE 15.1%)"
              py="30px"
              zIndex="10"
            >
              <Button variant="primary" width="220px" onClick={handleSubscribe} loading={isLoading}>
                Abonnieren
              </Button>
            </Box>
          )}
          <Flex
            width="100%"
            bg={{ base: 'background.primary', md: 'background.canvas' }}
            gap="30px"
            p={{ base: '30px 0px 0px', md: '45px 0px 30px' }}
            flexDirection="column"
          >
            <Box px="25px">
              <SubscriptionButtons
                activeButton={activeButton}
                onClick={setActiveButton}
                onSubscribe={handleSubscribe}
                isLoading={isLoading}
                subscriptionPrices={subscriptionPrices || []}
                promoCode={!isCouponExpired ? promoCode : undefined}
                referralInfos={referralInfos}
              />
            </Box>
            {shouldShowCouponBanner && (
              <Box
                background="yellow"
                p="10px 20px"
                backgroundColor="#FEF7E0"
                display="flex"
                flexDir="row"
                gap="5px"
                alignItems="center"
              >
                <PresentIcon
                  color="text.secondary"
                  height="20px"
                  width="20px"
                  transform="translateY(-1px)"
                />
                <Text textStyle="body.s.regular">Dein Promo-Code ist aktiv</Text>
                <NovaIcon icon={SuccessIcon} size="s" color="icon.accent" />
              </Box>
            )}
            <Box px="25px">
              <SubscriptionBenefits />
            </Box>
            <Box px="25px">
              <Text textStyle="body.xs.regular" textAlign="center">
                Mit deiner Bestellung erklärst du dich mit unseren{' '}
                <ExternalLink
                  href={ExternalRoutes.TERMS}
                  color="text.info"
                  target="_blank"
                  textStyle="body.xs.regular"
                >
                  Nutzungsbedingungen und Widerrufsbestimmungen
                </ExternalLink>{' '}
                einverstanden
              </Text>
            </Box>
          </Flex>
          {!isDesktop && (
            <Box>
              <SubscriptionSlider />
            </Box>
          )}
        </Flex>
      </Box>
      {isDesktop && (
        <Box
          position="absolute"
          height="500px"
          width="40%"
          left="100%"
          top="50%"
          transform="translateY(-50%) translateX(-100%)"
        >
          <SubscriptionSlider />
        </Box>
      )}
    </CustomModal>
  );
};
